import React, { useId, useState } from 'react';

import { errorMessage } from '../../services/apiError';
import axiosInstance from '../../services/axiosInstance';
import {
  forgetLoopBrowser, openLoopBrowserOptions, pairLoopBrowser, reconnectLoopBrowser,
} from '../../services/extensionBridge';
import type { LoopBrowserStatus } from '../../services/extensionBridge';
import { loopService } from '../../services/loopService';

import type { LoopsState } from './useLoops';
import './LoopBrowserPairing.css';

export const loopApiOrigin = () => new URL(
  axiosInstance.defaults?.baseURL || window.location.origin, window.location.origin,
).origin;

export const LOOP_RECOVERY_UPDATE_MESSAGE = 'Update the 8080.AI extension to 2.6.1 or later, reload it, then refresh this page to connect again.';

const sameLoopApi = (origin: string): boolean => {
  const configured = loopApiOrigin();
  const aliases = [
    ['http://localhost:3000', 'http://localhost:8000'],
    ['https://8080.ai', 'https://api.8080.ai'],
  ];

  return origin === configured || aliases.some((group) => group.includes(configured) && group.includes(origin));
};

// A saved profile ID proves ownership; these explicit aliases also recognize the web API proxy.
export const isCurrentLoopBrowser = (extension: LoopBrowserStatus | null, profileId: string): boolean => (
  Boolean(extension?.paired && extension.profile_id === profileId && sameLoopApi(extension.api_origin))
);

const currentBrowserName = () => {
  const browser = /Edg\//.test(navigator.userAgent) ? 'Edge' : /Chrome\//.test(navigator.userAgent) ? 'Chrome' : 'Browser';
  const device = /Macintosh|Mac OS X/.test(navigator.userAgent) ? 'Mac'
    : /Windows/.test(navigator.userAgent) ? 'Windows' : /Linux/.test(navigator.userAgent) ? 'Linux' : 'this device';

  return `${browser} on ${device}`;
};

interface Props {
  state: LoopsState;
  extension: LoopBrowserStatus | null;
  checking: boolean;
  refreshExtension: () => Promise<LoopBrowserStatus | null>;
}

// eslint-disable-next-line max-lines-per-function, max-statements
function ThisBrowser({ state, extension, checking, refreshExtension }: Props) {
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [pairedHere, setPairedHere] = useState<string | null>(null);

  // The direct response bridges the first profile refresh, then the server list owns its lifecycle.
  if (pairedHere && [...state.profiles, ...(state.revokedProfiles || [])]
    .some((profile) => profile.id === pairedHere)) setPairedHere(null);
  const revoked = state.revokedProfiles?.find((profile) => isCurrentLoopBrowser(extension, profile.id));
  const saved = state.profiles.find((profile) => profile.status !== 'revoked' && isCurrentLoopBrowser(extension, profile.id));
  const owned = saved ?? (!revoked && pairedHere && isCurrentLoopBrowser(extension, pairedHere)
    ? { id: pairedHere, name: extension?.name || 'This browser' } : undefined);
  const otherConnection = Boolean(extension?.paired && !owned && !revoked && !state.loading);
  const connected = Boolean(owned && extension?.connected && !extension.paused);
  const fail = (reason: unknown) => {
    setError(errorMessage(reason));

    return true;
  };
  // Recheck on the explicit click, so installing/reloading the extension does not require navigating away.
  // eslint-disable-next-line max-statements
  const connect = async () => {
    setError(''); setMessage('');
    const current = await refreshExtension();

    if (!current) throw new Error('Install the 8080.AI extension in this browser, then click Connect this browser again.');
    const disconnected = state.revokedProfiles?.find((profile) => isCurrentLoopBrowser(current, profile.id));

    if (disconnected && !current.revoked_pairing_recovery) throw new Error(LOOP_RECOVERY_UPDATE_MESSAGE);
    if (!current.one_click_pairing) throw new Error('Update the 8080.AI extension to 2.6.0 or later, then try again.');
    const existing = state.profiles.find((profile) => profile.status !== 'revoked' && isCurrentLoopBrowser(current, profile.id))
      ?? (!disconnected && pairedHere && isCurrentLoopBrowser(current, pairedHere) ? { id: pairedHere } : undefined);

    if (current.paired && !existing && !disconnected) {
      throw new Error('This browser already has another connection. Open extension settings to manage it before connecting here.');
    }
    if (disconnected) {
      await forgetLoopBrowser({ profileId: disconnected.id, apiOrigin: current.api_origin });
      const cleared = await refreshExtension();

      if (!cleared) throw new Error('The extension is unavailable. Refresh this page and click Connect this browser again.');
      if (cleared.paired) throw new Error('The browser connection changed. Refresh this page before connecting again.');
    }
    if (existing) await reconnectLoopBrowser({ profileId: existing.id, apiOrigin: current.api_origin });
    else {
      const name = currentBrowserName();
      const pairing = await loopService.pair(name);

      const result = await pairLoopBrowser({ pairingCode: pairing.pairing_code, name, apiOrigin: loopApiOrigin() });

      setPairedHere(result.profile_id);
    }
    await refreshExtension();
    setMessage('This browser is paired. Its connection will appear in your browser profiles shortly.');
  };

  return <div className="loop-pair-choice">
    <h4>Connect the browser you’re using now</h4>
    <p>Loops will run in this browser profile using its signed-in websites. No pairing code to copy.</p>
    {revoked && <p className="loop-muted">This browser was disconnected from your account.
      Connect again to use it for loops.</p>}
    {revoked && !extension?.revoked_pairing_recovery && <p className="loop-muted">{LOOP_RECOVERY_UPDATE_MESSAGE}</p>}
    {owned && <p className="loop-muted">Saved as <strong>{owned.name}</strong>.
      {extension?.paused ? ' This connection is paused.' : connected ? ' This browser is connected.'
        : ' Waiting for this browser to connect.'}</p>}
    {otherConnection ? <>
      <p className="loop-muted">This browser already has a connection outside this account or server.
        Manage that connection in the extension before pairing here.</p>
      <button type="button" disabled={state.busy} onClick={() => void state.perform(openLoopBrowserOptions, fail)}>
        Open extension settings</button>
    </> : <button type="button" className="loop-primary" disabled={state.busy || checking || state.loading || connected}
      onClick={() => void state.perform(connect, fail)}>
      {checking || state.loading ? 'Checking browser…' : state.busy ? 'Connecting…' : connected ? 'This browser is connected'
        : owned ? 'Reconnect this browser' : 'Connect this browser'}
    </button>}
    {!owned && !checking && !otherConnection && <p className="loop-muted">
      {extension ? !extension.one_click_pairing && 'Update your extension to 2.6.0 or later for one-click pairing.'
        : 'Install the 8080.AI extension in this browser first. Installation steps are below.'}
    </p>}
    {message && (owned || pairedHere) && !otherConnection && !revoked && <p className="loop-pair-success" role="status">
      {connected ? 'This browser is connected and ready for loops.' : message}</p>}
    {error && <p className="loop-error" role="alert">{error}</p>}
  </div>;
}

// eslint-disable-next-line max-lines-per-function
function AnotherBrowser({ state }: Pick<Props, 'state'>) {
  const [name, setName] = useState('');
  const [pair, setPair] = useState<{ pairing_code: string; expires_at: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const createPair = async () => { setCopied(false); setPair(await loopService.pair(name.trim())); };

  return <div className="loop-pair-choice">
    <h4>Connect another browser or device</h4>
    <p>Use a one-time pairing code for a different browser, browser profile, or computer.
      Install the extension in that browser, then paste the code into its Loops settings.</p>
    <form onSubmit={(event) => { event.preventDefault(); void state.perform(createPair); }}>
      <label className="loop-field"><span>Profile name</span><input required value={name} maxLength={120}
        placeholder="Work Chrome · another computer" onChange={(event) => setName(event.target.value)} /></label>
      <button type="submit" className="loop-primary" disabled={state.busy || !name.trim()}>Create pairing code</button>
    </form>
    {pair && <div className="loop-pairing">
      <p>Enter this code in the extension on the <strong>other browser</strong>.</p>
      <code aria-label="One-time pairing code">{pair.pairing_code}</code>
      <button type="button" disabled={state.busy} onClick={() => void state.perform(async () => {
        await navigator.clipboard.writeText(pair.pairing_code); setCopied(true);
      })}>{copied ? 'Copied' : 'Copy code'}</button>
      <small>Works once. Expires {new Date(pair.expires_at).toLocaleString()}.</small>
      <label className="loop-field"><span>API address for the other browser’s extension</span><input readOnly
        value={loopApiOrigin()} /></label>
      <p className="loop-muted">Keep the other browser open while its loops run.</p>
    </div>}
  </div>;
}

export function LoopBrowserPairing(props: Props) {
  const [target, setTarget] = useState<'this' | 'other'>('this');
  const id = useId();

  return <section className="loop-card loop-browser-pairing" aria-label="Connect a browser">
    <h3>Connect a browser</h3>
    <div className="loop-pair-target" role="group" aria-label="Browser to connect">
      <button type="button" aria-pressed={target === 'this'} aria-controls={id}
        disabled={props.state.busy} onClick={() => setTarget('this')}>This browser</button>
      <button type="button" aria-pressed={target === 'other'} aria-controls={id}
        disabled={props.state.busy} onClick={() => setTarget('other')}>Another browser</button>
    </div>
    <div id={id}>{target === 'this' ? <ThisBrowser {...props} /> : <AnotherBrowser state={props.state} />}</div>
  </section>;
}
